import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { FileText, Plus, Search, Edit2, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', category: 'Insights', tags: [], meta_description: '', og_image: '', published: false });
  const [saving, setSaving] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/blog/admin/posts?page=${page}`);
      setPosts(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openNew = () => {
    setEditingPost(null);
    setForm({ title: '', slug: '', excerpt: '', content: '', category: 'Insights', tags: [], meta_description: '', og_image: '', published: false });
    setShowEditor(true);
  };

  const openEdit = async (post) => {
    try {
      const { data } = await axios.get(`${API}/blog/posts/${post.slug}`);
      setEditingPost(data);
      setForm({ title: data.title, slug: data.slug, excerpt: data.excerpt || '', content: data.content || '', category: data.category || 'Insights', tags: data.tags || [], meta_description: data.meta_description || '', og_image: data.og_image || '', published: data.published });
      setShowEditor(true);
    } catch (err) { toast.error('Failed to load post'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPost) {
        await axios.patch(`${API}/blog/admin/posts/${editingPost.id}`, form);
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/blog/admin/posts`, form);
        toast.success('Post created');
      }
      setShowEditor(false);
      fetchPosts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await axios.delete(`${API}/blog/admin/posts/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch { toast.error('Failed to delete'); }
  };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <AdminLayout title="Blog Posts" breadcrumbs={[{ label: 'Blog' }]}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#4E6974]">{total} posts</p>
        <button onClick={openNew} data-testid="admin-blog-new-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00707C] text-white text-sm rounded-lg hover:bg-[#005F6A] transition-colors">
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-blog-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Title</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Category</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-8 text-[#B0BEC5]">Loading...</td></tr>}
            {!loading && posts.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#B0BEC5]">No posts yet</td></tr>}
            {posts.map(p => (
              <tr key={p.id} className="border-b border-[#F5F5F5]">
                <td className="px-4 py-3 font-medium text-[#0A2533]">{p.title}</td>
                <td className="px-4 py-3 text-[#4E6974] text-xs">{p.category}</td>
                <td className="px-4 py-3">
                  {p.published
                    ? <span className="text-xs text-green-600 flex items-center gap-1"><Eye className="h-3 w-3" /> Published</span>
                    : <span className="text-xs text-[#B0BEC5] flex items-center gap-1"><EyeOff className="h-3 w-3" /> Draft</span>}
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{p.published_at ? new Date(p.published_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <button onClick={() => openEdit(p)} className="text-[#0097A7] hover:text-[#005F6A]"><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {showEditor && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" data-testid="admin-blog-editor">
            <h2 className="text-lg font-bold text-[#0A2533] mb-4">{editingPost ? 'Edit post' : 'New post'}</h2>
            <div className="space-y-3">
              <input value={form.title} onChange={e => { setForm(f => ({...f, title: e.target.value})); if (!editingPost) setForm(f => ({...f, slug: autoSlug(e.target.value)})); }}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Post title" data-testid="admin-blog-title" />
              <input value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm font-mono focus:border-[#0097A7] outline-none" placeholder="url-slug" data-testid="admin-blog-slug" />
              <input value={form.excerpt} onChange={e => setForm(f => ({...f, excerpt: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Short excerpt (shown in cards)" />
              <textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none min-h-[200px] font-mono" placeholder="Article content (HTML)" data-testid="admin-blog-content" />
              <div className="grid grid-cols-2 gap-3">
                <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}
                  className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm" placeholder="Category" />
                <input value={form.meta_description} onChange={e => setForm(f => ({...f, meta_description: e.target.value}))}
                  className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm" placeholder="Meta description (SEO)" />
              </div>
              <input value={form.og_image} onChange={e => setForm(f => ({...f, og_image: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm" placeholder="Featured image URL (optional)" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({...f, published: e.target.checked}))} className="rounded" />
                Publish immediately
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditor(false)} className="flex-1 py-2 border border-[#E0E0E0] rounded-lg text-sm text-[#4E6974]">Cancel</button>
                <button onClick={handleSave} disabled={saving} data-testid="admin-blog-save"
                  className="flex-1 py-2 bg-[#00707C] text-white rounded-lg text-sm hover:bg-[#005F6A] disabled:bg-[#B0BEC5]">{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
